import http from './client';
import { errorHandler, parseApiError } from './errorHandler';
import type { ApiResponse } from './types';
import type { ProfileCapabilities } from './types';

// ─── Types ────────────────────────────────────────────────────────────────────

/** People who can access this profile (returned for “child” accounts with a guardian). */
export interface ProfileSharedWithEntry {
  userId: string;
  name: string;
  email: string;
  role: 'owner' | 'manager' | 'viewer';
  /** True when this row is the logged-in user (the profile subject). */
  isYou?: boolean;
}

export interface FamilyMember {
  id: string;
  uuid?: string | null;
  name: string;
  email: string;
  avatar?: string;
  dob: string;
  gender: string;
  relationship: string;
  isSelf: boolean;
  role: 'owner' | 'manager' | 'viewer';
  hasOwnAccount: boolean;
  inviteStatus: 'pending' | 'accepted' | null;
  hasPendingTransfer?: boolean;
  /** Latest resolved ownership transfer you requested (profile still has external owner until accepted). */
  ownershipTransferOutcome?: 'accepted' | 'rejected';
  /** When the guardian approved or declined (ISO), for dismissible UI. */
  ownershipTransferOutcomeAt?: string | null;
  /** Pending profile-share email invites you sent for this profile (server, matches access list). */
  pendingOutgoingShareInviteCount?: number;
  hasExternalOwner?: boolean;
  /** Guardian who currently owns this profile (when you were added as a dependent). */
  profileOwner?: { id: string; name: string; email: string } | null;
  /** Everyone with access to this profile, including you. */
  profileSharedWith?: ProfileSharedWithEntry[];
  /** Server-side capabilities resolved for the active actor on this profile. */
  capabilities?: ProfileCapabilities;
  account_type?: string;
}

export interface ProfileAccessUser {
  userId: string;
  name: string;
  email: string;
  role: 'owner' | 'manager' | 'viewer';
  since: string;
}

/** Outgoing profile share: waiting for recipient to accept or decline. */
export interface PendingProfileShareInvite {
  inviteId?: string;
  email: string;
  expiresAt: string;
}

/** Recipient declined the share invite (cancelled). */
export interface RejectedProfileShareInvite {
  email: string;
  rejectedAt: string;
}

export interface ProfileAccessListPayload {
  users: ProfileAccessUser[];
  pendingShareInvites: PendingProfileShareInvite[];
  rejectedShareInvites?: RejectedProfileShareInvite[];
  inviteHistory?: ProfileShareInviteHistoryEntry[];
}

export interface ProfileShareInviteHistoryEntry {
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled' | 'revoked';
  requestedRole: 'viewer' | 'manager';
  createdAt: string;
  updatedAt?: string;
  expiresAt: string;
}

export interface IncomingShareInvite {
  inviteId: string;
  inviterName: string;
  inviterEmail: string;
  profileId: string;
  profileName: string;
  inviteEmail: string;
  expiresAt: string;
  accessRole: 'viewer' | 'manager';
}

export interface InviteVerificationData {
  token?: string;
  name?: string;
  email?: string;
  dob?: string;
  gender?: string;
  relationship?: string;
  inviterName?: string;
  /** Optional member/profile id if backend provides it */
  memberId?: string;
}

export interface AddFamilyMemberPayload {
  name: string;
  email: string;
  dob: string;
  gender: string;
  relationship: string;
  /** True when member is a minor (< 18). Backend creates a DEPENDENT account — no email required. */
  isDependent?: boolean;
}

export interface UpdateFamilyMemberPayload {
  name?: string;
  email?: string;
  dob?: string;
  gender?: string;
  relationship?: string;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const familyApi = {
  /**
   * List family members scoped to the acting profile (see `X-Acting-Profile-Id`).
   * When provided, `actingProfileId` is sent on this request so it cannot race
   * with other tabs of `localStorage` updates during profile switches.
   */
  getMembers: async (
    actingProfileId?: string | null,
  ): Promise<ApiResponse<FamilyMember[]>> => {
    try {
      const pin = actingProfileId?.trim();
      const response = await http.get('/family/members', {
        ...(pin
          ? {
              headers: { 'X-Acting-Profile-Id': pin } satisfies Record<
                string,
                string
              >,
            }
          : {}),
      });
      const data = response.data as
        | { members?: FamilyMember[] }
        | FamilyMember[];
      const members = Array.isArray(data)
        ? data
        : ((data as { members?: FamilyMember[] }).members ?? []);
      return { success: true, data: members };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** Add a new family member profile */
  addMember: async (
    payload: AddFamilyMemberPayload,
  ): Promise<ApiResponse<FamilyMember>> => {
    try {
      const response = await http.post('/family/members', payload);
      const data = response.data as { member?: FamilyMember } | FamilyMember;
      const member =
        (data as { member?: FamilyMember }).member ?? (data as FamilyMember);
      return { success: true, data: member };
    } catch (error) {
      const { message, fieldErrors } = parseApiError(error);
      return { success: false, error: message, fieldErrors };
    }
  },

  /** Update a family member's profile details */
  updateMember: async (
    memberId: string,
    payload: UpdateFamilyMemberPayload,
  ): Promise<ApiResponse<FamilyMember>> => {
    try {
      const response = await http.put(`/family/members/${memberId}`, payload);
      const data = response.data as { member?: FamilyMember } | FamilyMember;
      const member =
        (data as { member?: FamilyMember }).member ?? (data as FamilyMember);
      return { success: true, data: member };
    } catch (error) {
      const { message, fieldErrors } = parseApiError(error);
      return { success: false, error: message, fieldErrors };
    }
  },

  /** Remove a family member link */
  deleteMember: async (memberId: string): Promise<ApiResponse<null>> => {
    try {
      await http.delete(`/family/members/${memberId}`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** Fetch full profile for a family member (profile switching) */
  getMemberProfile: async (
    memberId: string,
  ): Promise<ApiResponse<Partial<FamilyMember>>> => {
    try {
      const response = await http.get(`/family/members/${memberId}/profile`);
      const data = response.data as { member?: FamilyMember } | FamilyMember;
      const member =
        (data as { member?: FamilyMember }).member ?? (data as FamilyMember);
      return { success: true, data: member };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // ── Invite ─────────────────────────────────────────────────────────────────

  /** Send or resend invite email for a family member (age-gated ≥16) */
  sendInvite: async (
    memberId: string,
  ): Promise<ApiResponse<{ sentTo: string }>> => {
    try {
      const response = await http.post(`/family/members/${memberId}/invite`);
      return { success: true, data: response.data as { sentTo: string } };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Generate a claim token for a dependent account */
  generateClaimToken: async (
    memberId: string,
  ): Promise<ApiResponse<{ claimToken: string }>> => {
    try {
      const response = await http.post(
        `/family/members/${memberId}/claim-token`,
      );
      return { success: true, data: response.data as { claimToken: string } };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Verify a dependent claim token (used by the dependent when opening the claim link) */
  verifyDependentClaimToken: async (
    token: string,
  ): Promise<
    ApiResponse<{
      dependentProfile: {
        id: number;
        name: string;
        email?: string | null;
        dob?: string;
        gender?: string;
        country?: string | null;
        state?: string | null;
        city?: string | null;
        preferred_language?: string | null;
        street1?: string | null;
        street2?: string | null;
        zip?: string | null;
        avatar?: string | null;
      };
    }>
  > => {
    try {
      const response = await http.post('/family/claim/verify', { token });
      return {
        success: true,
        data: response.data as {
          dependentProfile: {
            id: number;
            name: string;
            dob?: string;
            gender?: string;
          };
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Verify a family invite token (to prefill signup data) */
  verifyInvite: async (
    token: string,
  ): Promise<
    ApiResponse<{
      memberId?: string;
      name: string;
      email: string;
      dob?: string;
      gender?: string;
    }>
  > => {
    try {
      const response = await http.post('/family/invite/verify', { token });
      return {
        success: true,
        data: response.data as {
          memberId?: string;
          name: string;
          email: string;
          dob?: string;
          gender?: string;
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Accept a family invite token (called after son completes sign-up) */
  acceptInvite: async (
    token: string,
  ): Promise<ApiResponse<{ profileId: string | number }>> => {
    try {
      const response = await http.post('/family/invite/accept', { token });
      return {
        success: true,
        data: response.data as { profileId: string | number },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  // ── Share & Access ──────────────────────────────────────────────────────────

  /** Share a profile with another user by email (default viewer; only owner can set manager) */
  shareProfile: async (
    memberId: string,
    email: string,
    role: 'viewer' | 'manager' = 'viewer',
  ): Promise<
    ApiResponse<{ sharedWith: string; role: string; pending?: boolean }>
  > => {
    try {
      const response = await http.post(`/family/members/${memberId}/share`, {
        email,
        role,
      });
      return {
        success: true,
        data: response.data as {
          sharedWith: string;
          role: string;
          pending?: boolean;
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Verify a share invite token before acceptance. */
  verifyShareInvite: async (
    token: string,
  ): Promise<
    ApiResponse<{
      inviterName: string;
      inviterEmail: string;
      profileName: string;
      inviteEmail: string;
      requestedRole: 'viewer' | 'manager';
    }>
  > => {
    try {
      const response = await http.post('/family/share/verify', { token });
      return {
        success: true,
        data: response.data as {
          inviterName: string;
          inviterEmail: string;
          profileName: string;
          inviteEmail: string;
          requestedRole: 'viewer' | 'manager';
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Accept a share invite token (existing user flow). */
  acceptShareInvite: async (
    token: string,
  ): Promise<
    ApiResponse<{ profileId: string; role: 'viewer' | 'manager' }>
  > => {
    try {
      const response = await http.post('/family/share/accept', { token });
      return {
        success: true,
        data: response.data as {
          profileId: string;
          role: 'viewer' | 'manager';
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** List pending incoming share requests for current user. */
  getIncomingShareInvites: async (): Promise<
    ApiResponse<IncomingShareInvite[]>
  > => {
    try {
      const response = await http.get('/family/share/incoming');
      const data = response.data as { invites?: IncomingShareInvite[] };
      return { success: true, data: data.invites ?? [] };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Accept or reject an incoming share request from in-app UI. */
  respondIncomingShareInvite: async (
    inviteId: string,
    action: 'accept' | 'reject',
  ): Promise<
    ApiResponse<{
      profileId?: string;
      role?: 'viewer' | 'manager';
      rejected?: boolean;
    }>
  > => {
    try {
      const response = await http.post('/family/share/respond', {
        inviteId,
        action,
      });
      return {
        success: true,
        data: response.data as {
          profileId?: string;
          role?: 'viewer' | 'manager';
          rejected?: boolean;
        },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Owner only: change shared access between viewer and manager */
  updateAccessRole: async (
    memberId: string,
    targetUserId: string,
    role: 'viewer' | 'manager',
  ): Promise<ApiResponse<{ userId: string; role: string }>> => {
    try {
      const response = await http.patch(
        `/family/members/${memberId}/access/${targetUserId}`,
        { role },
      );
      return {
        success: true,
        data: response.data as { userId: string; role: string },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Get list of all users with access to a profile (and pending share invites you sent). */
  getAccessList: async (
    memberId: string,
  ): Promise<ApiResponse<ProfileAccessListPayload>> => {
    try {
      const response = await http.get(`/family/members/${memberId}/access`);
      const raw = response.data as
        | ProfileAccessUser[]
        | ProfileAccessListPayload;
      if (Array.isArray(raw)) {
        return {
          success: true,
          data: {
            users: raw,
            pendingShareInvites: [],
            rejectedShareInvites: [],
            inviteHistory: [],
          },
        };
      }
      return {
        success: true,
        data: {
          users: raw.users ?? [],
          pendingShareInvites: raw.pendingShareInvites ?? [],
          rejectedShareInvites: raw.rejectedShareInvites ?? [],
          inviteHistory: raw.inviteHistory ?? [],
        },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** Revoke a specific user's access to a profile (owner only) */
  revokeAccess: async (
    memberId: string,
    targetUserId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.delete(`/family/members/${memberId}/access/${targetUserId}`);
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Sender-side: cancel a pending share invite they sent for this profile. */
  cancelShareInvite: async (
    memberId: string,
    inviteId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.delete(
        `/family/members/${memberId}/share-invites/${inviteId}`,
      );
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Recipient-side: remove own access from this profile. */
  removeMyAccess: async (memberId: string): Promise<ApiResponse<null>> => {
    try {
      await http.delete(`/family/members/${memberId}/access/self`);
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  // ── Ownership Transfer ──────────────────────────────────────────────────────

  /** Son requests ownership transfer (age-gated ≥18) */
  requestOwnershipTransfer: async (
    profileId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.post('/family/ownership/request', { profileId });
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Father approves ownership transfer */
  acceptOwnershipTransfer: async (
    profileId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.post('/family/ownership/accept', { profileId });
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  /** Father rejects ownership transfer */
  rejectOwnershipTransfer: async (
    profileId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.post('/family/ownership/reject', { profileId });
      return { success: true, data: null };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },
};
